import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, MenuItem, Select, styled, Typography, useTheme } from '@mui/material';
import { KingBed, Person } from '@mui/icons-material';

import { CounterItem } from '@frontend/features/common/components/CounterItem';
import { HandleRoomChangeProps, RoomSelection } from './types';
import { v4 as uuid } from 'uuid';

interface HotelSearchRoomInputWidgetItemProps {
  room: RoomSelection;
  roomIndex: number;
  onRoomChange: (props: HandleRoomChangeProps) => void;
}

interface Paxes {
  id: string;
  age: number;
}

const RoomsTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'stretch',
  gap: theme.spacing(1),
  py: theme.spacing(1),
  mb: theme.spacing(2),
  bgcolor: theme.palette.primaryAlternative.lighter,
}));

const getLastPaxIndex = (paxes: Paxes[], isAdult: boolean): number => {
  for (let i = paxes.length - 1; i >= 0; i--) {
    const {age} = paxes[i];
    if (isAdult ? age >= 18 : age < 18) {
      return i;
    }
  }
  return -1; // No se encontró un pax que cumpla la condición
};

export const HotelSearchRoomInputWidgetItem = ({
  room,
  roomIndex,
  onRoomChange,
}: HotelSearchRoomInputWidgetItemProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [paxes, setPaxes] = useState<Paxes[]>(room.paxes.length ? room.paxes.map((age) => ({ age, id: uuid()})) : [{ age: 18, id: uuid()}]);

  const handleAddPax = (isAdult: boolean) => {
    if (paxes.length < 4) {
      const age = isAdult ? 18 : 0;
      setPaxes((prevPaxes) => [...prevPaxes, { age, id: uuid()}]);
    }
  };

  const handleRemovePax = (isAdult: boolean) => {
    setPaxes((prevPaxes) => {
      const adults = prevPaxes.filter(({ age }) => age >= 18);

      if(isAdult && adults.length === 1) {
        return prevPaxes;
      }
      const indexToRemove = getLastPaxIndex(prevPaxes, isAdult);
      return prevPaxes.filter((_, i) => i !== indexToRemove);
    });
  };

  const handleAgeChange = (id: string, age: number) => {
    setPaxes((prevPaxes) => prevPaxes.map((pax) => (pax.id === id ? {...pax, age} : pax)));
  };

  useEffect(() => {
    onRoomChange({
      roomIndex,
      paxes: paxes.map(({ age }) => age)
    });
  }, [paxes]);

  const adults = paxes.filter(({ age }) => age >= 18);
  const minors = paxes.filter(({ age }) => age < 18);

  return (
    <Box my={theme.spacing(2)} gap={theme.spacing(1)}>
      <RoomsTitle>
        <KingBed color="primary" />
        {t('hotels.roomMenu.room')} {roomIndex + 1}
      </RoomsTitle>
      <CounterItem
        icon={<Person color="disabled" />}
        itemText={t('hotels.roomMenu.adults')}
        helperText={`+18 ${t('hotels.roomMenu.years')}`}
        amount={adults.length}
        onAdd={() => handleAddPax(true)}
        onRemove={() => handleRemovePax(true)}
      />
      <CounterItem
        icon={<KingBed color="disabled" />}
        itemText={t('hotels.roomMenu.minors')}
        helperText={`0 - 17  ${t('hotels.roomMenu.years')}`}
        amount={minors.length}
        onAdd={() => handleAddPax(false)}
        onRemove={() => handleRemovePax(false)}
      />

      {adults.map(({ age, id }, index) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={2}
          key={id}
          padding={theme.spacing(1, 3)}
        >
          <Typography>
            {t('hotels.roomMenu.adultAge', { index: index +1  })}
          </Typography>
          <Select
            fullWidth
            size='small'
            value={age}
            onChange={(e) => handleAgeChange(id, Number(e.target.value))}
          >
            {
              Array.from({ length: 83 }, (_, age) => (
                <MenuItem key={age + 18} value={age + 18}>
                  {t('hotels.roomMenu.yearsOld', { count: age + 18 })}
                </MenuItem>
              ))
            }
          </Select>
        </Box>
      ))}   
      {minors.map(({age, id}, index) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={2}
          key={id}
          padding={theme.spacing(1, 3)}
        >
          <Typography>
            {t('hotels.roomMenu.minorAge', { index: index +1  })}
          </Typography>
          <Select
            fullWidth
            size='small'
            value={age}
            onChange={(e) => handleAgeChange(id, Number(e.target.value))}
          >
            {
              Array.from({ length: 18 }, (_, age)  => (
                <MenuItem key={age} value={age}>
                  { age === 0 ? t('hotels.roomMenu.lessThanOneYear') : t('hotels.roomMenu.yearsOld', { count: age })}
                </MenuItem>
              ))
            }
          </Select>
        </Box>
      ))}   
    </Box>
  );
};
